import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PhoneSchema } from '@capsico/contracts';
import { fetchAreaVotes, voteForArea } from '../api/customer';
import { errorMessage } from '../api/client';
import { turnstileErrorKind } from '../api/turnstile';
import { deviceId } from '../api/device';
import { useI18n } from '../i18n';
import { pointKey } from './useDeliveryPoint';
import { useTurnstile } from './useTurnstile';

/**
 * "Bring Capsico here" where we do not deliver yet. Guests can vote (one per device per ~1 km);
 * signed-in customers are notified on their own number, guests may leave one.
 */
export function useAreaVote(point: { lat: number; lng: number }, signedIn: boolean) {
  const client = useQueryClient();
  const { t } = useI18n();
  // W25: browser builds prove a person voted (Turnstile); the native app sends no token.
  const turnstile = useTurnstile();
  const key = ['area-votes', pointKey(point)];
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Turnstile refusals are shown under the check itself, not under the phone field.
  const [checkError, setCheckError] = useState<string | null>(null);
  const count = useQuery({
    queryKey: key,
    queryFn: () => fetchAreaVotes(point),
    staleTime: 60_000,
  });

  const vote = useMutation({
    mutationFn: (token: string | null) => {
      const digits = phone.replace(/[^\d+]/g, '');
      const notifyPhone =
        digits === '' ? undefined : digits.startsWith('+') ? digits : `+91${digits}`;
      return voteForArea(
        {
          lat: point.lat,
          lng: point.lng,
          deviceId: deviceId(),
          ...(notifyPhone && !signedIn ? { notifyPhone } : {}),
        },
        token,
      );
    },
    // Turnstile tokens are single-use: get a fresh check after every attempt.
    onSettled: () => turnstile.reset(),
    onMutate: async () => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<{ votesNearby: number }>(key);
      if (previous) client.setQueryData(key, { votesNearby: previous.votesNearby + 1 });
      return { previous };
    },
    onError: (failure, _v, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
      const refused = turnstileErrorKind(failure);
      if (refused) setCheckError(t(`turnstile.${refused}`));
      else setError(errorMessage(failure));
    },
    onSuccess: (result) => client.setQueryData(key, { votesNearby: result.votesNearby }),
  });

  return {
    votesNearby: count.data?.votesNearby ?? null,
    phone,
    setPhone: (value: string) => {
      setError(null);
      setPhone(value.replace(/[^\d+ ]/g, '').slice(0, 16));
    },
    showPhone: !signedIn,
    submit: () => {
      const digits = phone.replace(/[^\d+]/g, '');
      if (
        digits &&
        !PhoneSchema.safeParse(digits.startsWith('+') ? digits : `+91${digits}`).success
      ) {
        setError('Enter a valid Indian or Nepali mobile number');
        return;
      }
      if (turnstile.enabled && !turnstile.token) {
        setCheckError(t('turnstile.required'));
        return;
      }
      setError(null);
      setCheckError(null);
      vote.mutate(turnstile.enabled ? turnstile.token : null);
    },
    voting: vote.isPending,
    result: vote.data ?? null,
    error,
    turnstile: {
      enabled: turnstile.enabled,
      ref: turnstile.ref,
      error: checkError ?? (turnstile.failed ? t('turnstile.failed') : null),
    },
  };
}
